import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { TeamProgress, TeamProgressStep } from '../../utils/teamProgress';
import styles from './TeamProgressBar.module.scss';

export interface TeamProgressBarPropTypes {
  progress: TeamProgress;
  // Override the "Next up:" label suffix; defaults to i18n-resolved step name.
  nextUpLabel?: string;
  // When true, the team has not started yet (no teamStartDate — e.g. an incomplete
  // team still waiting for members). Nothing past Product Purchase can be acted on,
  // so no step past Purchase is shown "in progress" (orange) and the "Next up" hint
  // is suppressed. Completed steps (e.g. Purchase) still render green.
  locked?: boolean;
  // When true, the campaign has ended: any step that isn't complete can no longer be
  // worked on, so it's shown red (incomplete) instead of orange/gray, and the
  // "Next up" hint is hidden.
  campaignEnded?: boolean;
}

const STEP_LABEL_KEY: Record<TeamProgressStep, string> = {
  [TeamProgressStep.PURCHASE]: 'dashboard.home.label.stepPurchase',
  [TeamProgressStep.PRE_ASSESSMENT]: 'dashboard.home.label.stepPreAssessment',
  [TeamProgressStep.CHECK_IN]: 'dashboard.home.label.stepCheckIn',
  [TeamProgressStep.POST_ASSESSMENT]: 'dashboard.home.label.stepPostAssessment',
  [TeamProgressStep.RESULT]: 'dashboard.home.label.stepResult',
};

// The single step the user/team can act on next (first incomplete actionable step).
// Check-In is in progress only when it is configured (isAllCheckIn !== null) and not yet
// complete. Result is gated, so it is never "in progress".
const getUnlockedStep = (progress: TeamProgress, locked = false): TeamProgressStep | null => {
  if (!progress.isAllPurchase) return TeamProgressStep.PURCHASE;
  // The team hasn't started yet: Purchase is done but nothing past it can be acted
  // on until the team is complete/started, so there is no in-progress step.
  if (locked) return null;
  if (!progress.isAllWeightIn) return TeamProgressStep.PRE_ASSESSMENT;
  if (progress.isAllCheckIn === false) return TeamProgressStep.CHECK_IN;
  if (!progress.isAllWeightOut) return TeamProgressStep.POST_ASSESSMENT;
  return null;
};

const segmentClass = (
  complete: boolean | null,
  isInProgress: boolean,
  failed?: boolean,
  campaignEnded?: boolean,
): string => {
  if (complete === true) return styles['green-color'];
  // Completed-but-not-won (Overall Results) shows red. Once the campaign has ended,
  // an evaluated-but-incomplete step (complete === false) is also red — but a grey
  // placeholder (complete === null, e.g. check-in not configured) stays grey.
  if (failed || (campaignEnded && complete === false)) return styles['red-color'];
  if (isInProgress) return styles['orange-color'];
  // Locked / not-yet-reached steps stay gray (no red).
  return styles['gray-color'];
};

const TeamProgressBar: FC<TeamProgressBarPropTypes> = ({
  progress,
  nextUpLabel,
  locked = false,
  campaignEnded = false,
}) => {
  const { t } = useTranslation();

  const stepsText = t('dashboard.home.label.stepsCompleted')
    .replace('{current-step}', progress.completedSteps.toString())
    .replace('{total-steps}', progress.totalSteps.toString());

  // Suppress the "Next up" hint when the team hasn't started (not actionable yet) or
  // the campaign has ended (nothing more can be done).
  const resolvedNextUp =
    locked || campaignEnded ? null : nextUpLabel ?? (progress.nextStepKey ? t(progress.nextStepKey) : null);

  // No "in progress" step once the campaign has ended (or the team is locked).
  const unlockedStep = campaignEnded ? null : getUnlockedStep(progress, locked);

  return (
    <div className={styles['team-progress-bar']} data-testid="team-progress-bar">
      <div className={styles['steps-text']}>{stepsText}</div>
      <div className={styles['segments']}>
        {progress.steps.map(s => (
          <div key={s.step} className={styles['segment-item']} title={t(STEP_LABEL_KEY[s.step])}>
            <div
              className={`${styles['single-bar']} ${segmentClass(
                s.complete,
                s.step === unlockedStep,
                s.failed,
                campaignEnded,
              )}`}
            />
          </div>
        ))}
      </div>
      {resolvedNextUp && (
        <div className={styles['next-up']}>
          {/* The value already reads "Next step: X" (see nextStep* i18n), so the
              "Next up:" label prefix would be redundant and is omitted. */}
          <span className={styles['next-up-value']}>{resolvedNextUp}</span>
        </div>
      )}
    </div>
  );
};

TeamProgressBar.displayName = 'TeamProgressBar';
export default TeamProgressBar;
